import type { Effort } from "../api";
import { useT } from "../i18n";

/**
 * EffortChips is how hard the model is asked to think about a translation
 * (requirement 0042, ADR-0097): three pill chips, at every press that spends.
 *
 * **Chips rather than a menu**, like every other choice in the studio: a native
 * select on a phone covers the screen with the operating system's own list, and
 * the thing being chosen between is three words.
 *
 * The offered set comes from the server (`translationStyle`) rather than from a
 * list here, so the chips and the closed set the server validates against cannot
 * drift — a fourth chip would spend at a level that is then refused.
 */
export default function EffortChips({
  value,
  offered,
  onChange,
  disabled,
  testIdPrefix,
}: {
  value: Effort;
  offered: Effort[];
  onChange: (effort: Effort) => void;
  disabled?: boolean;
  testIdPrefix: string;
}) {
  const t = useT();
  const label: Record<Effort, string> = {
    fast: t.translation.effortFast,
    medium: t.translation.effortMedium,
    high: t.translation.effortHigh,
  };

  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={t.translation.effortHeading}>
      {offered.map((effort) => (
        <button
          key={effort}
          type="button"
          data-testid={`${testIdPrefix}-${effort}`}
          aria-pressed={effort === value}
          disabled={disabled}
          onClick={() => onChange(effort)}
          className={`rounded-full border px-4 py-2 text-sm transition-colors disabled:opacity-50 ${
            effort === value ? "border-sage bg-sage-soft font-medium" : "border-line hover:bg-sunk"
          }`}
        >
          {label[effort]}
        </button>
      ))}
    </div>
  );
}
